<template>
  <div
    :contenteditable="editable"
    class="prose max-w-full p-2"
    :class="{format: !inheritStyles}"
    @blur="onEdit"
    @click="onClick"
    v-html="markdownToHtml"
  />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {marked} from 'marked';

/** Schemes a link may carry. Anything else can run script on a click. */
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Render a string as text, whatever it contains.
 */
const escape = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * A link target that cannot execute anything.
 *
 * `javascript:` and `data:` URLs run on a click, and an unescaped quote closes
 * the attribute and opens another one. Applied to every caller, trusted or
 * not: no content this component renders has a reason to link to a script.
 */
const safeHref = (href: string): string => {
    try {
        // Resolved against the current document so a relative link keeps
        // working; `URL.parse` would read better but is too recent for every
        // shell in the field.
        const {protocol} = new URL(href, window.location.href);

        return SAFE_SCHEMES.includes(protocol) ? escape(href) : '';
    } catch {
        return '';
    }
};

export default defineComponent({
    name: 'MarkdownView',
    props: {
        content: {
            type: String,
            required: true
        },
        inheritStyles: {
            type: Boolean,
            default: false
        },
        /**
         * Whether the content was written by something other than Apodis.
         *
         * Raw HTML is then rendered as the text it is. It is off by default
         * because most callers render content authored in a back-office, where
         * HTML is a legitimate formatting vocabulary — and because a component
         * cannot tell where its content came from, only its caller can.
         */
        untrusted: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    emits: ['on-edit'],
    computed: {
        markdownToHtml() {
            const renderer = new marked.Renderer();

            renderer.link = ({href, text}) => {
                const target = safeHref(href);

                return target
                    ? `<a href="${target}" target="_blank" rel="noopener noreferrer">${text}</a>`
                    : text;
            };

            // A model's answer is inserted with `v-html` into a desktop
            // renderer, and what it writes is steerable — a pharmacy's own
            // free-text instructions reach every turn and are read by the whole
            // team. Markdown is the formatting vocabulary there; HTML is not
            // part of it.
            if (this.untrusted) {
                renderer.html = ({text}) => escape(text);
            }

            return marked(this.content, {renderer});
        }
    },
    methods: {
        onEdit (event: any) {
            if (this.editable) {
                this.$emit('on-edit', event.target?.innerHTML);
            }
        },
        /**
         * Open an external link in the default browser.
         *
         * The main window has no window open handler, so a `target="_blank"`
         * link would otherwise open the site in a bare Electron window.
         */
        onClick (event: MouseEvent) {
            const link = (event.target as HTMLElement | null)?.closest('a');

            if (this.editable || !link || !window.ipcRenderer?.openInBrowser) {
                return;
            }

            if (new URL(link.href).origin !== window.location.origin) {
                event.preventDefault();
                window.ipcRenderer.openInBrowser(link.href);
            }
        }
    }
});
</script>

<style scoped>

.prose :deep(strong) {
    @apply text-inherit;
}

@tailwind components

@layer components {
    .format h1 {
        @apply text-2xl font-bold mb-0 font-heading !important;
    }

    .format h2 {
        @apply text-xl font-bold my-0 font-heading !important;
    }

    .format p {
        @apply font-content !important;
    }

    .format li {
        @apply text-sm !important;
    }

    .prose blockquote p {
        content: '' !important;
        font-style: normal !important;
    }

    .prose blockquote p::after {
        content: '' !important;
    }

    .prose blockquote p::before {
        content: '' !important;
    }
}
</style>
